// MARK://dhk8/pos/lib/branding-loader
// Runtime branding. The shell ships with a build-time default brand
// (import.meta.env.VITE_BRAND, injected by @dockethub/vite-plugin-brand), then the
// backend's `brand{}` block (from /me/branding, or the activate/login response)
// can refine it per-tenant: tenant tint colors, display name, logo URL.
// Nothing about identity is hardcoded in components — they read getBranding().
//
// ADR-0019/0032. Backend chain: dhk8-platform-tenants.TenantBranding.

import { svcUrl } from './api.js'

export interface Branding {
  /** Commercial brand id (matches data-brand / theme-<id>.css). */
  brand: string
  /** Display name shown in headers / titles. */
  name: string
  /** Tenant logo URL, or null to use the brand default mark. */
  logoUrl: string | null
  /** Optional per-tenant tint overrides (CSS custom properties → value). */
  tint?: Record<string, string>
}

const BUILD_BRAND = (import.meta.env.VITE_BRAND as string | undefined) ?? 'nintaigo'
const BUILD_BRAND_NAME = (import.meta.env.VITE_BRAND_NAME as string | undefined) ?? 'NintaiGO'

let current: Branding = {
  brand: BUILD_BRAND,
  name: BUILD_BRAND_NAME,
  logoUrl: null,
}

interface BackendBrandBlock {
  brand?: string
  name?: string
  displayName?: string
  logoUrl?: string
  tint?: Record<string, string>
}

function apply(b: Branding): void {
  current = b
  if (typeof document !== 'undefined') {
    const root = document.documentElement
    if (b.brand) root.dataset.brand = b.brand
    if (b.tint) for (const [k, v] of Object.entries(b.tint)) root.style.setProperty(k, v)
  }
}

/** Last known branding. Synchronous; safe to call from render. */
export function getBranding(): Branding {
  return current
}

/**
 * Fetch and apply the tenant `brand{}` block from the backend. Best-effort:
 * on any failure the build-time brand stays in effect. Call once after auth.
 */
export async function loadBranding(signal?: AbortSignal): Promise<Branding> {
  try {
    const res = await fetch(svcUrl('dhk8-platform-tenants', '/v1/me/branding'), { credentials: 'include', signal })
    if (!res.ok) return current
    const data = (await res.json()) as { brand?: BackendBrandBlock } | BackendBrandBlock
    const block: BackendBrandBlock = 'brand' in data && typeof data.brand === 'object' ? (data.brand as BackendBrandBlock) : (data as BackendBrandBlock)
    apply({
      brand: typeof block.brand === 'string' ? block.brand : current.brand,
      name: block.displayName ?? block.name ?? current.name,
      logoUrl: block.logoUrl ?? null,
      ...(block.tint ? { tint: block.tint } : {}),
    })
  } catch {
    // keep build-time defaults
  }
  return current
}
